import { useEffect, useState } from 'react';

import {
  Accordion,
  CircleCheckSolidIcon,
  CircleInfoSolidIcon,
  DiamondExclamationIcon,
  TriangleExclamationIcon,
  XmarkIcon,
} from '../..';
import { addStyles } from '../../utils/helpers';

export type AlertType = 'info' | 'warning' | 'error' | 'success';
export type AlertSize = 'medium' | 'large';

export interface AlertProps {
  /** Controls the styling of the alert */
  type: AlertType;
  /** Bolded text that is displayed above the alert message */
  title?: string;
  /** The alert message which can be either a string or ReactNode */
  content: string | React.ReactNode;
  /** An element to display after the content. */
  action?: React.ReactNode;
  /** Is combined with existing classes but does not override existing styles. */
  className?: string;
  /** Flag to control whether to display the circle exclamation icon */
  showIcon?: boolean;
  /** Adds an accordion element which replaces the dismiss with an arrow */
  collapsibleContent?: string | React.ReactNode;
  /** The default collapsed state */
  isCollapsed?: boolean;
  /** Called whenever collapsed state is toggled */
  onOpenChange?: (isCollapsed: boolean) => void;
  /** Disabling this removes the x button. */
  isDismissible?: boolean;
  /** Callback when clicking the close button */
  onVisibleChange?: (isVisible: boolean) => void;
  /** Controls the visibility of the alert */
  isVisible?: boolean;
  /** Controls the height of the alert */
  size?: AlertSize;
}

interface AlertContentProps {
  type: AlertType;
  title?: string;
  content: string | React.ReactNode;
  action?: React.ReactNode;
  showIcon?: boolean;
  size?: AlertSize;
}

const options = {
  base: 'border-0.5 border-l-8 rounded-lg text-gray-900 w-full',
  contentWrapper: 'flex items-start gap-2 py-1',
  type: {
    info: 'bg-blue-50 border-blue-300 fill-blue-700',
    error: 'bg-red-50 border-red-300 fill-red-500',
    warning: 'bg-gold-50 border-gold-300 fill-gold-700',
    success: 'bg-green-50 border-green-300 fill-green-700',
  },
  size: {
    large: 'p-4',
    medium: 'p-2 text-sm',
  },
  collapseWrapper: {
    large: 'ml-7 mr-9 mt-4',
    medium: 'ml-7 mr-9 mt-2',
  },
};

const AlertContent = (props: AlertContentProps) => {
  const { showIcon = true, type, title, content, action, size } = props;

  const icons = {
    info: CircleInfoSolidIcon,
    success: CircleCheckSolidIcon,
    warning: TriangleExclamationIcon,
    error: DiamondExclamationIcon,
  };

  return (
    <div className={options.contentWrapper}>
      {showIcon && (
        <div data-testid="Alert icon" className="flex items-center">
          {icons[type]({ className: size === 'large' ? 'size-6 inline-block' : 'size-5 inline-block' })}
        </div>
      )}
      <div className="flex-start flex flex-1 flex-wrap gap-1">
        {title && <div className="font-bold">{title}</div>}
        <div>{content}</div>
        <div>{action}</div>
      </div>
    </div>
  );
};

export const Alert = (props: AlertProps) => {
  const {
    type,
    className = '',
    title = '',
    collapsibleContent,
    isDismissible = true,
    onVisibleChange,
    onOpenChange,
    isCollapsed = false,
    isVisible: defaultVisibility = true,
    size = 'large',
    ...rest
  } = props;
  const [isVisible, setIsVisible] = useState(defaultVisibility);

  useEffect(() => setIsVisible(defaultVisibility), [defaultVisibility]);

  if (!isVisible) return <></>;

  return (
    <div className={addStyles([options.base, options.type[type], options.size[size], className])}>
      {collapsibleContent ? (
        <Accordion
          isHeaderToggleButton={false}
          onOpenChange={onOpenChange}
          isOpen={!isCollapsed}
          header={<AlertContent type={type} title={title} size={size} {...rest} />}
          content={<div className={options.collapseWrapper[size]}>{collapsibleContent}</div>}
          collapseIconClasses={size === 'medium' ? 'h-7' : 'h-8'}
        />
      ) : (
        <div className="flex w-full">
          <AlertContent type={type} title={title} size={size} {...rest} />
          {isDismissible && (
            <button
              className="ml-auto flex items-start"
              aria-label="Dismiss alert"
              onClick={() => {
                setIsVisible(false);
                onVisibleChange?.(false);
              }}
            >
              <XmarkIcon className={`${size === 'medium' ? 'size-7' : 'size-8'} fill-gray-900`} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

Alert.displayName = 'Alert';
export default Alert;
